import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { BrandPreviewPhone, pickHeaderLogo } from "@/components/corporate/organizations/BrandPreviewPhone";
import { brandWarnings, isValidHex, type BrandWarning, type BrandWarningCode } from "@/lib/brandContrast";
import { LOGO_MAX_SAMPLE_WIDTH, analyseLogoPixels, loadLogoPixels, type LogoPixels } from "@/lib/logoContrast";
import { cropToContent, downsamplePixels } from "@/lib/logoFraming";
import { AlertTriangle, Info, X } from "lucide-react";

export type AppBrandingColourField = "primaryColor" | "secondaryColor" | "accentColor";

/**
 * Where the "Header logo size" slider stands (org-header-logo-fit, D23/D24/D26):
 * - `ready` — usable;
 * - `measuring` — a saved wide logo's current size is being read (disabled, no note);
 * - `saved-unreadable` — the saved logo cannot be read in the browser (disabled + re-upload note);
 * - `unframeable` — the picked file cannot be framed, e.g. SVG/corrupt (disabled + file-type note);
 * - `swap-pending` — a resize was half-saved (old logo deleted, new upload failed); the
 *   resized logo is kept and the next Save finishes it (disabled + "not saved yet" note);
 * - `no-wide-logo` — the slider is replaced by the "Add a wide logo" note.
 */
export type HeaderLogoSizeState = "ready" | "measuring" | "saved-unreadable" | "unframeable" | "swap-pending" | "no-wide-logo";

/** The opt-in header-logo-size control. Only the Edit organisation dialog passes it; it owns the state. */
export interface HeaderLogoSizeControl {
  /** Zoom 0.6–1 (shown as 60–100 %). */
  value: number;
  onChange: (zoom: number) => void;
  state: HeaderLogoSizeState;
  /**
   * The pending framing's trimmed content pixels (no padding, full resolution).
   * When given for the wide logo being shown, the logo visibility check judges
   * these — downsampled to ≤ `LOGO_MAX_SAMPLE_WIDTH` — instead of reading the
   * padded framed file, so "own background ⇒ ok" (D17) still holds.
   */
  trimmed?: ImageData | null;
}

export const HEADER_LOGO_SIZE_LABEL = "Header logo size";

const HEADER_LOGO_SIZE_NOTES: Partial<Record<HeaderLogoSizeState, string>> = {
  "saved-unreadable": "To change the size, upload the logo file again",
  unframeable: "Size can't be adjusted for this file type",
  "swap-pending": "Resized logo not saved yet — select Save to finish",
};

export const NO_WIDE_LOGO_NOTE = "Add a wide logo to control the header size";

function HeaderLogoSize({ idPrefix, control }: { idPrefix: string; control: HeaderLogoSizeControl }) {
  if (control.state === "no-wide-logo") {
    return (
      <p data-testid="header-logo-size" className="text-xs text-muted-foreground">
        {NO_WIDE_LOGO_NOTE}
      </p>
    );
  }
  const percent = Math.round(control.value * 100);
  const note = HEADER_LOGO_SIZE_NOTES[control.state];
  const noteId = `${idPrefix}-header-logo-size-note`;
  return (
    <div data-testid="header-logo-size" className="space-y-2">
      <div className="flex items-center justify-between">
        <Label id={`${idPrefix}-header-logo-size-label`}>{HEADER_LOGO_SIZE_LABEL}</Label>
        <span data-testid="header-logo-size-value" className="text-sm tabular-nums text-muted-foreground">
          {percent} %
        </span>
      </div>
      <Slider
        thumbLabel={HEADER_LOGO_SIZE_LABEL}
        min={60}
        max={100}
        step={5}
        value={[percent]}
        disabled={control.state !== "ready"}
        aria-describedby={note ? noteId : undefined}
        onValueChange={([v]) => control.onChange(Number((v / 100).toFixed(2)))}
      />
      {note && (
        <p id={noteId} data-testid="header-logo-size-note" className="text-xs text-muted-foreground">
          {note}
        </p>
      )}
    </div>
  );
}

export const HEX_COLOUR_ERROR = "Use a 6-digit hex colour like #1C3446, or leave it empty.";

/**
 * Format errors for the three colour fields (the CMS API only accepts
 * `^#[0-9A-Fa-f]{6}$`). Empty is allowed. This is input validation, not a
 * readability warning — readability warnings never block saving (D5).
 */
export function appBrandingColourErrors(
  values: Record<AppBrandingColourField, string | null | undefined>,
): Partial<Record<AppBrandingColourField, string>> {
  const errors: Partial<Record<AppBrandingColourField, string>> = {};
  (Object.keys(values) as AppBrandingColourField[]).forEach((field) => {
    const value = (values[field] ?? "").trim();
    if (value !== "" && !isValidHex(value)) errors[field] = HEX_COLOUR_ERROR;
  });
  return errors;
}

interface AppBrandingEditorProps {
  /** Prefix for element ids — keeps ids unique when two editors could co-exist. */
  idPrefix: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  employeeAppBrandingEnabled: boolean;
  /** The organisation is on the Athlyn app (its package) — only then can the app take its branding. */
  usesAthlynApp: boolean;
  onColourChange: (field: AppBrandingColourField, value: string) => void;
  onEnabledChange: (enabled: boolean) => void;
  /** Square logo: pending file (preferred) and saved URL. */
  logoFile?: File | null;
  logoUrl?: string | null;
  /** Wide home-header logo: pending file (preferred) and saved URL. */
  wideLogoFile?: File | null;
  wideLogoUrl?: string | null;
  organizationName?: string;
  /** Opt-in "Header logo size" slider above the preview (Edit organisation only). */
  headerLogoSize?: HeaderLogoSizeControl;
  /**
   * Opt-in (company page): no readability warnings while the app branding is locked (not on the
   * Athlyn app) — the app never shows these colours, so the warnings would be noise.
   */
  hideWarningsWhenLocked?: boolean;
}

interface ColourFieldProps {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  helper?: string;
  error?: string;
  clearable?: boolean;
}

function ColourField({
  id,
  label,
  value,
  placeholder,
  onChange,
  helper,
  error,
  clearable = false,
}: ColourFieldProps) {
  const trimmed = value.trim();
  const hasColour = isValidHex(trimmed);
  const describedBy = [error ? `${id}-error` : null, helper ? `${id}-helper` : null]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        {/* Swatch: the native colour input cannot be empty (it shows black), so it sits
            invisibly over our own swatch, which is dashed/empty when no colour is set. */}
        <div
          data-testid={`${id}-swatch`}
          data-empty={hasColour ? "false" : "true"}
          className={
            hasColour
              ? "relative h-10 w-16 shrink-0 rounded-md border border-input"
              : "relative h-10 w-16 shrink-0 rounded-md border border-dashed border-input bg-muted"
          }
          style={hasColour ? { backgroundColor: trimmed } : undefined}
        >
          <input
            type="color"
            aria-label={`${label} picker`}
            value={hasColour ? trimmed.toLowerCase() : "#ffffff"}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={7}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={describedBy || undefined}
          className={error ? "border-destructive" : undefined}
        />
        {clearable && value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => onChange("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {error}
        </p>
      )}
      {helper && (
        <p id={`${id}-helper`} className="text-xs text-muted-foreground">
          {helper}
        </p>
      )}
    </div>
  );
}

const BRAND_WARNING_TITLES: Record<BrandWarningCode, string> = {
  MAIN_TOO_LIGHT: "Main colour too light",
  HIGHLIGHT_TEXT_WEAK: "Highlight colour too light",
  MAIN_HIGHLIGHT_TOO_SIMILAR: "Main and highlight too similar",
  ACCENT_MAIN_TOO_SIMILAR: "Accent too close to the main colour",
  NEAR_ERROR_RED: "Looks like the error colour",
  NEAR_SUCCESS_GREEN: "Looks like the success colour",
  ACCENT_DERIVED: "Accent derived from highlight",
  SWITCH_ON_COLOURS_MISSING: "Colours missing",
};

/** Title for a warning, derived from its code (never hard-coded per severity). */
export function brandWarningTitle(code: BrandWarningCode): string {
  return BRAND_WARNING_TITLES[code];
}

/** Logo visibility results (D17) — their own codes and titles, separate from the colour warnings. */
export type LogoWarningCode = "LOGO_LOW_CONTRAST" | "LOGO_CHECK_UNAVAILABLE";

export interface LogoWarning {
  code: LogoWarningCode;
  severity: "warning" | "info";
  message: string;
}

const LOGO_WARNINGS: Record<LogoWarningCode, LogoWarning & { title: string }> = {
  LOGO_LOW_CONTRAST: {
    code: "LOGO_LOW_CONTRAST",
    severity: "warning",
    title: "Logo may be hard to see",
    message: "This logo may be hard to see on the main colour. Upload a version with its own background.",
  },
  LOGO_CHECK_UNAVAILABLE: {
    code: "LOGO_CHECK_UNAVAILABLE",
    severity: "info",
    title: "Logo not checked",
    message: "We couldn't check the saved logo automatically — check the preview.",
  },
};

/** Same code and title as the saved-logo note, worded for a picked file that is not uploaded yet. */
const PENDING_LOGO_CHECK_UNAVAILABLE: LogoWarning = {
  ...LOGO_WARNINGS.LOGO_CHECK_UNAVAILABLE,
  message: "We couldn't check this logo automatically — check the preview.",
};

export function logoWarningTitle(code: LogoWarningCode): string {
  return LOGO_WARNINGS[code].title;
}

/** One rendered alert; brand and logo warnings share the look, not their codes. */
interface DisplayWarning {
  key: string;
  testId: string;
  severity: BrandWarning["severity"];
  message: string;
  /** Always shown for danger; for other severities only when given (logo warnings). */
  title?: string;
}

function WarningItem({ warning }: { warning: DisplayWarning }) {
  const title = warning.title ? <AlertTitle>{warning.title}</AlertTitle> : null;
  if (warning.severity === "danger") {
    return (
      <Alert variant="destructive" data-testid={warning.testId}>
        <AlertTriangle className="h-4 w-4" />
        {title}
        <AlertDescription>{warning.message}</AlertDescription>
      </Alert>
    );
  }
  if (warning.severity === "warning") {
    return (
      <Alert className="border-warning" data-testid={warning.testId}>
        <AlertTriangle className="h-4 w-4 !text-warning" />
        {title}
        <AlertDescription>{warning.message}</AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert data-testid={warning.testId}>
      <Info className="h-4 w-4 !text-muted-foreground" />
      {title}
      <AlertDescription className="text-muted-foreground">{warning.message}</AlertDescription>
    </Alert>
  );
}

/**
 * Organisation colours + the "Brand the employee app" switch, with readability
 * warnings (never blocking — D5) and a live phone preview. Shared by the Edit
 * organisation dialog and the Add organisation wizard.
 */
export function AppBrandingEditor({
  idPrefix,
  primaryColor,
  secondaryColor,
  accentColor,
  employeeAppBrandingEnabled,
  usesAthlynApp,
  onColourChange,
  onEnabledChange,
  logoFile,
  logoUrl,
  wideLogoFile,
  wideLogoUrl,
  organizationName,
  headerLogoSize,
  hideWarningsWhenLocked = false,
}: AppBrandingEditorProps) {
  const warnings = useMemo(
    () =>
      brandWarnings({
        main: primaryColor,
        highlight: secondaryColor,
        accent: accentColor,
        enabled: employeeAppBrandingEnabled && usesAthlynApp,
      }),
    [primaryColor, secondaryColor, accentColor, employeeAppBrandingEnabled, usesAthlynApp],
  );

  const errors = appBrandingColourErrors({ primaryColor, secondaryColor, accentColor });

  // Logo visibility check (D17): the logo the app's home header shows (wide, else
  // square) against the main colour — regardless of the switch, only for a valid
  // main colour, never blocking. Pixels are read once per logo; the analysis
  // re-runs on main-colour changes. Results of a superseded logo are dropped.
  // The WIDE logo is judged on its trimmed content (org-header-logo-fit): a pending
  // framing's `trimmed` pixels (downsampled) instead of the padded framed file, else
  // the loaded pixels cropped to their content box. The square logo is read as is.
  const headerLogo = pickHeaderLogo({ wideLogoFile, wideLogoUrl, logoFile, logoUrl });
  const headerLogoSource = headerLogo?.source ?? null;
  const headerLogoIsWide = headerLogo?.variant === "wide";
  const mainIsValid = isValidHex(primaryColor);
  const pendingTrimmed =
    headerLogoIsWide && headerLogoSource && typeof headerLogoSource !== "string"
      ? (headerLogoSize?.trimmed ?? null)
      : null;
  const trimmedSample = useMemo(
    () => (pendingTrimmed ? downsamplePixels(pendingTrimmed, LOGO_MAX_SAMPLE_WIDTH) : null),
    [pendingTrimmed],
  );
  const usesTrimmed = trimmedSample !== null;
  const [logoPixels, setLogoPixels] = useState<{ source: File | string; pixels: LogoPixels } | null>(null);

  useEffect(() => {
    setLogoPixels(null);
    if (!headerLogoSource || !mainIsValid || usesTrimmed) return;
    let cancelled = false;
    loadLogoPixels(headerLogoSource).then(
      (loaded) => {
        const pixels: LogoPixels =
          headerLogoIsWide && loaded.status === "ok" ? { status: "ok", ...cropToContent(loaded) } : loaded;
        if (!cancelled) setLogoPixels({ source: headerLogoSource, pixels });
      },
      () => {
        if (!cancelled) setLogoPixels({ source: headerLogoSource, pixels: { status: "unavailable" } });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [headerLogoSource, headerLogoIsWide, mainIsValid, usesTrimmed]);

  const logoWarnings = useMemo<LogoWarning[]>(() => {
    if (trimmedSample) {
      if (!mainIsValid) return [];
      return analyseLogoPixels(trimmedSample.data, primaryColor) === "low-contrast"
        ? [LOGO_WARNINGS.LOGO_LOW_CONTRAST]
        : [];
    }
    if (!logoPixels || logoPixels.source !== headerLogoSource || !mainIsValid) return [];
    if (logoPixels.pixels.status === "unavailable") {
      // Saved logo (e.g. no storage CORS) vs a picked file the browser cannot decode (e.g. SVG, corrupt PNG).
      return [
        typeof logoPixels.source === "string"
          ? LOGO_WARNINGS.LOGO_CHECK_UNAVAILABLE
          : PENDING_LOGO_CHECK_UNAVAILABLE,
      ];
    }
    return analyseLogoPixels(logoPixels.pixels.data, primaryColor) === "low-contrast"
      ? [LOGO_WARNINGS.LOGO_LOW_CONTRAST]
      : [];
  }, [trimmedSample, logoPixels, headerLogoSource, mainIsValid, primaryColor]);

  // Merged for display only — the colour warnings stay exactly as brandWarnings() returns them.
  const displayWarnings: DisplayWarning[] = [
    ...warnings.map((w) => ({
      key: `${w.code}-${w.role ?? ""}`,
      testId: `brand-warning-${w.code}`,
      severity: w.severity,
      message: w.message,
      title: w.severity === "danger" ? brandWarningTitle(w.code) : undefined,
    })),
    ...logoWarnings.map((w) => ({
      key: w.code,
      testId: `logo-warning-${w.code}`,
      severity: w.severity,
      message: w.message,
      title: logoWarningTitle(w.code),
    })),
  ];

  return (
    // Fields and the 390-pt phone side by side at lg and above (the phone column takes
    // its natural width), stacked below; the preview column may shrink — it then scrolls.
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_auto]" data-testid="app-branding-editor">
      <div className="min-w-0 space-y-4">
        <ColourField
          id={`${idPrefix}-primary-color`}
          label="Main colour"
          value={primaryColor}
          placeholder="#1C3446"
          error={errors.primaryColor}
          onChange={(v) => onColourChange("primaryColor", v)}
          helper="Header, headings and numbers in the app."
        />
        <ColourField
          id={`${idPrefix}-secondary-color`}
          label="Highlight colour"
          value={secondaryColor}
          placeholder="#008DA2"
          error={errors.secondaryColor}
          onChange={(v) => onColourChange("secondaryColor", v)}
          helper="Buttons, selected chips, links and tags."
        />
        <ColourField
          id={`${idPrefix}-accent-color`}
          label="Accent colour (optional)"
          value={accentColor}
          placeholder="Derived from highlight"
          error={errors.accentColor}
          onChange={(v) => onColourChange("accentColor", v)}
          helper="Leave empty to use a darker shade of the highlight"
          clearable
        />

        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor={`${idPrefix}-app-branding-enabled`}>Brand the employee app</Label>
            <p id={`${idPrefix}-app-branding-enabled-note`} className="text-xs text-muted-foreground">
              {!usesAthlynApp
                ? "Companies on their own app use that app's look"
                : "Employees of this organisation see its logo and colours in the Athlyn app (light mode). Off by default."}
            </p>
          </div>
          <Switch
            id={`${idPrefix}-app-branding-enabled`}
            aria-describedby={`${idPrefix}-app-branding-enabled-note`}
            // Not on the Athlyn app: shown off (their own app never takes it) — the stored value is untouched.
            checked={employeeAppBrandingEnabled && usesAthlynApp}
            disabled={!usesAthlynApp}
            onCheckedChange={onEnabledChange}
          />
        </div>

        {displayWarnings.length > 0 && !(hideWarningsWhenLocked && !usesAthlynApp) && (
          <div className="space-y-2" data-testid="brand-warnings">
            {displayWarnings.map((w) => (
              <WarningItem key={w.key} warning={w} />
            ))}
          </div>
        )}
      </div>

      <div className="min-w-0 space-y-4">
        {headerLogoSize && <HeaderLogoSize idPrefix={idPrefix} control={headerLogoSize} />}
        <BrandPreviewPhone
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          accentColor={accentColor}
          enabled={employeeAppBrandingEnabled && usesAthlynApp}
          logoFile={logoFile}
          logoUrl={logoUrl}
          wideLogoFile={wideLogoFile}
          wideLogoUrl={wideLogoUrl}
          organizationName={organizationName}
        />
      </div>
    </div>
  );
}

export default AppBrandingEditor;
